/** Strukturierter JUDICATURE-Nachweis (n8n → Portal, strikt getrennt von LEXCORE). */

export interface JudicatureProofCase {
  case_id: string;
  court: 'OGH';
  geschaeftszahl: string;
  date?: string;
  leitsatz?: string;
  rechtssaetze?: string[];
  kernsaetze?: string[];
  norm_refs?: string[];
  source_url: string;
  score?: number;
}

export interface JudicatureProof {
  status: 'ok' | 'no_match';
  audit_id: string;
  snapshot_version?: string;
  jud_stamp: string;
  context_terms?: string[];
  cases: JudicatureProofCase[];
}

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function textList(value: unknown, maxItems: number): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value.map(text).filter(Boolean).slice(0, maxItems);
  return items.length > 0 ? items : undefined;
}

function parseCase(raw: unknown): JudicatureProofCase | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const caseId = text(o.case_id);
  const court = text(o.court).toUpperCase();
  const geschaeftszahl = text(o.geschaeftszahl);
  const sourceUrl = text(o.source_url);
  if (!/^OGH-/i.test(caseId) || court !== 'OGH' || !geschaeftszahl) return null;
  if (!sourceUrl.startsWith('https://www.ris.bka.gv.at/')) return null;
  const score = Number(o.score);
  return {
    case_id: caseId,
    court: 'OGH',
    geschaeftszahl,
    date: text(o.date) || undefined,
    leitsatz: text(o.leitsatz) || undefined,
    rechtssaetze: textList(o.rechtssaetze, 2),
    kernsaetze: textList(o.kernsaetze, 2),
    norm_refs: textList(o.norm_refs, 8),
    source_url: sourceUrl,
    score: Number.isFinite(score) ? score : undefined,
  };
}

export function parseJudicatureProof(raw: unknown): JudicatureProof | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const status = text(o.status);
  const auditId = text(o.audit_id);
  const stamp = text(o.jud_stamp);
  if (status !== 'ok' && status !== 'no_match') return null;
  if (!/^sha256:[0-9a-f]{64}$/i.test(auditId)) return null;
  if (!/^\[JUD:[0-9A-F]{16}\]$/.test(stamp)) return null;
  const cases = (Array.isArray(o.cases) ? o.cases : [])
    .map(parseCase)
    .filter((item): item is JudicatureProofCase => item !== null);
  return {
    status,
    audit_id: auditId,
    snapshot_version: text(o.snapshot_version) || undefined,
    jud_stamp: stamp,
    context_terms: textList(o.context_terms, 8),
    cases,
  };
}

export function hasJudicatureProofData(proof?: JudicatureProof | null): boolean {
  return parseJudicatureProof(proof ?? null) !== null;
}

export function isVerifiedJudicatureProof(proof?: JudicatureProof | null): boolean {
  const parsed = parseJudicatureProof(proof ?? null);
  return Boolean(parsed && parsed.status === 'ok' && parsed.cases.length > 0);
}

/** Der Stempel bleibt im ausklappbaren OGH-Nachweis sichtbar, nicht doppelt im Fließtext. */
export function stripJudicatureStampForDisplay(textValue: string): string {
  return textValue.replace(/\n*\[JUD:[^\]]+\](?=\s|$)/i, '').trim();
}
